import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  GraduationCap,
  BookOpen,
  Users,
  Building2,
  Handshake,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const inert = (e) => e.preventDefault();

const Container = ({ children, className = "" }) => (
  <div className={`relative mx-auto w-full max-w-[1288px] px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* Brand bar motif — derived from the WFII logo (vertical bars + teal square) */
const LogoBars = ({ dark = false, className = "" }) => {
  const bar = dark ? "bg-white/15" : "bg-[hsl(var(--primary))]/15";
  return (
    <div className={`flex items-end gap-1 ${className}`} aria-hidden="true">
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 28 }} />
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 15 }} />
      <span className={`w-1 rounded-[2px] ${bar}`} style={{ height: 22 }} />
      <span className="w-1 rounded-[2px] bg-[hsl(var(--accent))]/50" style={{ height: 10 }} />
    </div>
  );
};

/* ================= Block 1 — Hero ================= */

const MembershipHero = () => (
  <section data-testid="membership-hero-section">
    <Container className="pt-[41px] sm:pt-[49px] lg:pt-[90px]">
      <div className="mx-auto max-w-[62rem] text-center">
        <div className="flex items-center justify-center gap-3" aria-hidden="true">
          <span className="h-px w-10 bg-[hsl(var(--primary))]/25" />
          <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
          <span className="text-xs font-medium uppercase tracking-[0.22em] text-muted-foreground">
            WFII Membership
          </span>
          <span className="h-[3px] w-3 bg-[hsl(var(--accent))]" />
          <span className="h-px w-10 bg-[hsl(var(--primary))]/25" />
        </div>
        <h1 className="font-serif-editorial mx-auto mt-8 text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground [text-wrap:balance] sm:text-5xl lg:text-[3.6rem]">
          Join a Professional Community for Modern Finance Education
        </h1>
        <p className="mx-auto mt-8 max-w-[74ch] text-base leading-relaxed text-muted-foreground sm:text-lg lg:text-xl">
          WFII membership is designed for professionals and organizations that
          want ongoing access to digital finance education, resources,
          research, and industry conversations.
        </p>
        <p className="mx-auto mt-4 max-w-[74ch] text-base leading-relaxed text-muted-foreground sm:text-lg lg:max-w-none">
          Members receive preferred access to programs, practical materials,
          webinars, and updates across{" "}
          <br className="hidden lg:block" />
          stablecoins, digital assets, payments, accounting workflows,
          compliance, and{" "}
          <br className="lg:hidden" />
          financial infrastructure.
        </p>
        <div
          className="mx-auto mt-9 flex w-fit items-center gap-2.5 text-sm font-medium text-foreground sm:text-base"
          data-testid="membership-hero-enrollment-banner"
        >
          <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--accent))] opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[hsl(var(--accent))]" />
          </span>
          {"January 2027 program applications"}
          <br className="lg:hidden" />
          {" are open until December 23, 2026."}
        </div>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
          <Button
            asChild
            className="h-12 bg-[hsl(var(--accent))] px-8 text-sm font-medium text-white shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.45)] transition-colors hover:bg-primary active:scale-[0.98]"
            data-testid="membership-hero-become-member-button"
          >
            <Link to="/enrollment-request" state={{ type: "membership" }}>Become a Member</Link>
          </Button>
          <Link
            to="/programs"
            className="group inline-flex h-12 items-center gap-2 text-sm font-medium text-foreground transition-colors hover:text-[hsl(var(--accent))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
            data-testid="membership-hero-explore-programs-link"
          >
            Explore Programs
            <ArrowRight
              className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
              strokeWidth={1.75}
            />
          </Link>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Block 2 — Membership options ================= */

const TIERS = [
  {
    id: "individual",
    title: "Individual Membership",
    desc: "For finance professionals, accountants, bookkeepers, auditors, assurance professionals, CFOs, students, and founders building knowledge in digital finance.",
    includesLabel: "Includes",
    includes: [
      "Selected course access",
      "Member webinars",
      "Research library access",
      "Templates and checklists",
      "Certification discounts",
      "Member badge",
      "Community access",
    ],
    featured: false,
  },
  {
    id: "corporate",
    title: "Corporate Membership",
    desc: "For companies, accounting and audit firms, fintech teams, and finance departments building internal knowledge around modern finance.",
    includesLabel: "Corporate membership includes",
    includes: [
      "Employee seats",
      "Private training sessions",
      "Research briefings",
      "Roundtable participation",
      "Early access to guides",
      "Corporate member listing",
    ],
    featured: true,
  },
];

const MembershipOptions = () => (
  <section id="membership-options" data-testid="membership-options-section" className="!mt-[65px] lg:!mt-[120px]">
    <Container className="">
      <h2 className="text-center text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
        Membership Options
      </h2>
      <div className="mt-[36px] grid gap-6 lg:mt-14 lg:grid-cols-2 lg:max-w-[900px] lg:mx-auto">
        {TIERS.map((t) =>
          t.featured ? (
            <div
              key={t.id}
              className="wfii-stipple-dark relative overflow-hidden rounded-2xl bg-primary p-7 text-primary-foreground shadow-[0_32px_64px_-24px_hsl(218_51%_15%/0.55)] sm:p-9 lg:px-[31px]"
              data-testid={`membership-tier-${t.id}`}
            >
              <div className="relative flex h-full flex-col">
                <span className="block h-[3px] w-10 bg-[hsl(var(--accent))]" aria-hidden="true" />
                <h3 className="mt-7 text-xl font-semibold tracking-[-0.01em]">{t.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-primary-foreground/75 sm:text-lg lg:min-h-[120px]">
                  {t.desc}
                </p>
                <div className="mt-7 border-t border-white/10 pt-7 lg:mt-[18px]">
                  <span className="text-xs font-medium uppercase tracking-[0.14em] text-primary-foreground/60">
                    {t.includesLabel}
                  </span>
                  <ul className="mt-4 space-y-3">
                    {t.includes.map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <Check
                          className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--accent))]"
                          strokeWidth={2}
                          aria-hidden="true"
                        />
                        <span className="text-base leading-relaxed text-primary-foreground/85">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div
              key={t.id}
              className={`flex flex-col rounded-2xl bg-card/80 p-7 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] transition-shadow hover:shadow-[0_24px_48px_-24px_hsl(218_51%_15%/0.38)] sm:p-9 ${t.id === "individual" ? "lg:px-[25px]" : "lg:px-[31px]"}`}
              data-testid={`membership-tier-${t.id}`}
            >
              <span className="block h-[3px] w-10 bg-[hsl(var(--primary))]/20" aria-hidden="true" />
              <h3 className="mt-7 text-xl font-semibold tracking-[-0.01em] text-foreground">
                {t.title}
              </h3>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground sm:text-lg lg:min-h-[120px]">
                {t.desc}
              </p>
              <div className="mt-7 border-t border-[hsl(var(--primary))]/10 pt-7 lg:mt-[18px]">
                <span className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  {t.includesLabel}
                </span>
                <ul className="mt-4 space-y-3">
                  {t.includes.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Check
                        className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--wfii-teal))]"
                        strokeWidth={2}
                        aria-hidden="true"
                      />
                      <span className="text-base leading-relaxed text-muted-foreground">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )
        )}
      </div>
    </Container>
  </section>
);

/* ================= Block 3 — Membership fees ================= */

const MembershipFees = () => (
  <section data-testid="membership-fees-section">
    <Container className="">
      <h2 className="text-center text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
        Membership Fees
      </h2>
      <div className="mt-[36px] lg:mt-14">
        <div
          className="rounded-2xl bg-card/80 p-8 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] sm:p-10 lg:flex lg:items-center lg:gap-12"
          data-testid="membership-fees-individual"
        >
          <div className="shrink-0 lg:w-[210px]">
            <span className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Individual Membership
            </span>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-4xl font-semibold tracking-[-0.02em] text-foreground">
                CAD $39
              </span>
              <span className="text-sm text-muted-foreground">per year</span>
            </div>
          </div>
          <div className="mt-6 lg:mt-0 lg:flex-1">
            <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
              Get ongoing access to member resources, selected programs,
              webinars, research materials, templates, community opportunities,
              and the WFII{" "}
              <br className="hidden lg:block" aria-hidden="true" />
              member badge.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Applications are reviewed before approval.{" "}
              <br className="lg:hidden" />
              The membership fee is
              charged only after your{" "}
              <br className="hidden lg:block" aria-hidden="true" />
              application has been approved.
            </p>
          </div>
          <Button
            asChild
            className="mt-7 h-11 w-full shrink-0 bg-[hsl(var(--accent))] px-6 text-sm font-medium text-white shadow-[0_10px_24px_-8px_hsl(174_86%_30%/0.45)] transition-colors hover:bg-primary active:scale-[0.98] lg:mt-0 lg:w-auto"
            data-testid="membership-fees-individual-cta"
          >
            <Link to="/enrollment-request" state={{ type: "membership" }}>
              Apply for Membership
            </Link>
          </Button>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Block 4 — Member benefits ================= */

const BENEFITS = [
  {
    n: "01",
    icon: GraduationCap,
    title: "Professional Learning",
    body: "Access programs, webinars, and educational materials designed for finance and business professionals.",
  },
  {
    n: "02",
    icon: BookOpen,
    title: "Research and Resources",
    body: (<>Use guides, templates, checklists, glossaries, and briefings to understand digital finance topics with more clarity.</>),
  },
  {
    n: "03",
    icon: Users,
    title: "Community and Recognition",
    body: (
      <>
        Join a professional network focused on responsible digital finance
        education and receive{" "}
        <br className="lg:hidden" />
        a WFII member badge.
      </>
    ),
  },
  {
    n: "04",
    icon: Building2,
    title: "Corporate Education",
    body: "Support team learning through employee seats, private sessions, research briefings, and early access to new materials.",
  },
  {
    n: "05",
    icon: Handshake,
    title: "Industry Contribution",
    body: "Partners can contribute expertise, support research, participate in roundtables, and help advance market education.",
  },
];

const MemberBenefits = () => (
  <section data-testid="membership-benefits-section">
    <Container className="">
      <h2 className="text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-center lg:text-5xl">
        Member Benefits
      </h2>
      <div className="mt-[41px] grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:mt-14 lg:grid-cols-3">
        {BENEFITS.map((b) => (
          <div
            key={b.n}
            className="group border-t border-[hsl(var(--accent))] pt-6"
            data-testid={`membership-benefit-${b.n}`}
          >
            <div className="flex items-center justify-between">
              <b.icon
                className="h-6 w-6 text-[hsl(var(--wfii-teal))]"
                strokeWidth={1.75}
                aria-hidden="true"
              />
              <span
                className="h-2 w-2 rounded-[2px] bg-[hsl(var(--accent))]/0 transition-colors group-hover:bg-[hsl(var(--accent))]/80"
                aria-hidden="true"
              />
            </div>
            <h3 className="mt-4 text-[20px] font-semibold text-foreground">{b.title}</h3>
            <p className="mt-3 text-[16px] leading-relaxed text-muted-foreground">{b.body}</p>
          </div>
        ))}
      </div>
    </Container>
  </section>
);

/* ================= Block 4 — Who should join ================= */

const FIT_ITEMS = [
  "Finance professionals building knowledge in stablecoins, digital assets, payments, and accounting workflows",
  "Accounting and bookkeeping professionals supporting clients with modern finance questions",
  "CFOs and business leaders evaluating digital finance adoption",
  "Compliance, treasury, and payment teams preparing for new operational questions",
  "Auditors and assurance professionals evaluating digital asset records, evidence, valuation, controls, and reporting",
  "Organizations contributing to responsible digital finance education",
];

const WhoShouldJoin = () => (
  <section data-testid="membership-fit-section" className="!mt-[65px] lg:!mt-[120px]">
    <Container className="">
      <div className="grid gap-10 lg:grid-cols-[1fr_600px] lg:items-start lg:gap-16">
        <div>
          <span className="block h-[3px] w-12 bg-[hsl(var(--accent))]" aria-hidden="true" />
          <h2 className="mt-6 text-3xl font-semibold tracking-[-0.02em] text-foreground sm:text-4xl lg:text-5xl">
            Is WFII Membership{" "}
            <br className="hidden lg:block" />
            <br className="lg:hidden" />
            Right for You?
          </h2>
          <p className="mt-[35px] text-base leading-relaxed text-muted-foreground sm:text-lg lg:mt-10">
            WFII membership is a strong fit for professionals and organizations
            that want to understand digital finance in a clear and
            business-focused way.
          </p>
        </div>
        <div
          className="rounded-2xl bg-card/80 p-7 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px] sm:p-8 lg:px-[30px]"
          data-testid="membership-fit-card"
        >
          <span className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Membership is a strong fit for
          </span>
          <ul className="mt-4 space-y-3">
            {FIT_ITEMS.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <Check
                  className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--wfii-teal))]"
                  strokeWidth={2}
                  aria-hidden="true"
                />
                <span className="text-base leading-relaxed text-foreground/85">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Block 5 — Final CTA ================= */

const MembershipFinalCta = () => (
  <section data-testid="membership-final-cta-section">
    <Container className="pb-[60px] lg:pb-[120px]">
      <div className="wfii-stipple-dark relative overflow-hidden rounded-3xl bg-primary p-10 text-center text-primary-foreground shadow-[0_40px_80px_-32px_hsl(218_51%_15%/0.55)] sm:p-16">
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -left-40 -bottom-52 h-[480px] w-[480px]"
          aria-hidden="true"
        />
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -right-48 -top-48 h-[420px] w-[420px]"
          aria-hidden="true"
        />
        <LogoBars dark className="pointer-events-none absolute bottom-10 left-10 hidden sm:flex" />
        <LogoBars dark className="pointer-events-none absolute right-10 top-10 hidden rotate-180 sm:flex" />
        <div className="relative">
          <h2 className="font-serif-editorial mx-auto max-w-3xl text-3xl font-semibold leading-[1.12] tracking-[-0.02em] sm:text-4xl lg:text-5xl">
            Connect With the Future{" "}
            <br className="hidden lg:block" />
            of Business Finance
          </h2>
          <div className="mx-auto max-w-2xl">
            <p className="mt-7 text-base leading-relaxed text-primary-foreground/75 sm:text-lg">
              Membership gives professionals and organizations a structured way
              to stay connected to modern finance education, resources, and
              industry expertise.
            </p>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ================= Page ================= */

export default function MembershipPage() {
  return (
    <div className="relative min-h-screen">
      <div className="wfii-canvas wfii-noise" aria-hidden="true" data-testid="page-canvas" />
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <main className="flex-1 space-y-[60px] lg:space-y-[120px]">
          <MembershipHero />
          <MembershipOptions />
          <MemberBenefits />
          <WhoShouldJoin />
          <MembershipFees />
          <MembershipFinalCta />
        </main>
        <Footer />
      </div>
    </div>
  );
}
